import _ from "lodash";
import { Box, Button, HStack, Text, useTheme, View, VStack } from "native-base";
import React, { useState } from "react";
import type { TFunction } from "react-i18next";

import { CommonNumberInputWithPlusMinusButtons } from "../commons";
import { commonStyles, VerticalScale } from "../constants";
import type {
  GeneratedRecipeMeal,
  GeneratedRecipeMealIngredient,
  Ingredient,
  RecipeMeal,
} from "../services/backendTypes";
import { areIngredientsEqual, MemoizedIngredientComponent } from "./IngredientComponent";

type MealForEditScreenBase = GeneratedRecipeMeal | RecipeMeal;
type MealForEditScreen = MealForEditScreenBase & {
  portions_to_plan?: number;
};

type IngredientsRouteProps = {
  editable: boolean;
  portionsEditable: boolean;
  bufferedRecipeMeal: MealForEditScreen;
  onEditPortions: (portions: number) => Promise<void>;
  onEditIngredientQuantity: (ingredient: Ingredient, quantity: number) => Promise<void>;
  handleAddIngredient: () => void;
  handleEditIngredient: (ingredient: Ingredient) => void;
  showLoading: boolean;
  t: TFunction;
};

const IngredientsRoute = ({
  editable,
  portionsEditable,
  bufferedRecipeMeal,
  onEditPortions,
  onEditIngredientQuantity,
  handleAddIngredient,
  handleEditIngredient,
  showLoading,
  t,
}: IngredientsRouteProps): JSX.Element => {
  const theme = useTheme();

  const [allIngredientQuantityInputState, setAllIngredientQuantityInputState] = useState<{
    [ingredientIndex: number]: string;
  }>({});

  return (
    <Box pt={8}>
      <HStack justifyContent="flex-end" alignItems="center">
        <Text fontSize="lg" fontWeight="500" mr={2.5}>
          {t("recipe_detail.number_of_people_title")}
        </Text>

        {portionsEditable ? (
          <CommonNumberInputWithPlusMinusButtons
            editable={true}
            value={bufferedRecipeMeal.portions_to_plan || bufferedRecipeMeal.portions}
            min={1}
            step={1}
            onChange={onEditPortions}
            testIdPrefix={"number-of-people"}
          />
        ) : (
          <Text fontSize="lg" fontWeight="500">
            {bufferedRecipeMeal.portions_to_plan || bufferedRecipeMeal.portions}
          </Text>
        )}
      </HStack>

      <Text fontSize="lg" fontWeight="600" color="gray.800" mt={4} mb={4}>
        {t("recipe_detail.ingredients_tab_title")}
      </Text>

      <VStack space={1}>
        {bufferedRecipeMeal?.ingredients.map((ingredient, ingredientIndex) => {
          const quantityInputState = allIngredientQuantityInputState[ingredientIndex] || "";

          const setQuantityInputState = (newQuantityInputState: string): void => {
            setAllIngredientQuantityInputState({
              ...allIngredientQuantityInputState,
              [ingredientIndex]: newQuantityInputState,
            });
          };

          return (
            <MemoizedIngredientComponent
              key={`${ingredientIndex}-${ingredient.quantity}`}
              {...{
                ingredientIndex,
                ingredient,
                portions: bufferedRecipeMeal.portions,
                editable,
                onEditIngredientQuantity,
                t,
                quantityInputState,
                setQuantityInputState,
                onPressIngredientSwapButton: () => handleEditIngredient(ingredient as Ingredient),
                isLoading: showLoading,
              }}
            />
          );
        })}
      </VStack>

      {editable ? (
        <Button
          borderColor="gray.100"
          variant="outline"
          testID={"recipeDetailAddIngredient-button"}
          onPress={handleAddIngredient}
          mt={6}
        >
          <Text color="gray.600">{`+ ${t("recipe_detail.add_an_ingredient_button_text")}`}</Text>
        </Button>
      ) : null}
    </Box>
  );
};

const areIngredientsArrayEqual = (
  ingredientsA: GeneratedRecipeMealIngredient[] | Ingredient[],
  ingredientsB: GeneratedRecipeMealIngredient[] | Ingredient[]
): boolean => {
  if (ingredientsA.length !== ingredientsB.length) {
    return false;
  }

  return _.every(ingredientsA, (ingredientA: GeneratedRecipeMealIngredient | Ingredient, ingredientIndex: number) => {
    const ingredientB = ingredientsB[ingredientIndex];

    if (!ingredientB) return false;

    return areIngredientsEqual(ingredientA, ingredientB);
  });
};

export const MemoizedIngredientsRoute = React.memo(
  IngredientsRoute,
  (prevProps, nextProps) =>
    // NOTE: This is because `id` does not exist on GeneratedRecipeMeal
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    prevProps.bufferedRecipeMeal.id &&
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    prevProps.bufferedRecipeMeal.id === nextProps.bufferedRecipeMeal.id &&
    (prevProps.bufferedRecipeMeal.portions_to_plan || prevProps.bufferedRecipeMeal.portions) ===
      (nextProps.bufferedRecipeMeal.portions_to_plan || nextProps.bufferedRecipeMeal.portions) &&
    areIngredientsArrayEqual(prevProps.bufferedRecipeMeal.ingredients, nextProps.bufferedRecipeMeal.ingredients)
);

export default MemoizedIngredientsRoute;
